/**
 * API配置文件
 * 管理不同环境的API配置和常量
 */

// ================================
// 环境配置
// ================================
const ENV = process.env.NODE_ENV || 'development'

// API基础URL配置
const API_BASE_URLS = {
  development: 'http://116.169.116.71:8011/api',
  test: 'http://test-api.example.com/api',
  staging: 'http://staging-api.example.com/api',
  production: 'https://wechat.ipaycenter.cn:8005/api'
}

// 获取当前环境的API基础URL
export const getBaseURL = () => {
  return API_BASE_URLS[ENV] || API_BASE_URLS.development
}

// ================================
// 请求配置常量
// ================================
export const REQUEST_CONFIG = {
  // 请求超时时间（毫秒）
  TIMEOUT: 10000,

  // 重试配置
  RETRY: {
    MAX_RETRIES: 3,
    DELAY: 1000
  },

  // 缓存配置
  CACHE: {
    DEFAULT_TTL: 5 * 60 * 1000, // 5分钟
    USER_INFO_TTL: 30 * 60 * 1000, // 30分钟
    COMPANY_INFO_TTL: 60 * 60 * 1000 // 1小时
  },

  // 文件上传配置
  UPLOAD: {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    ALLOWED_TYPES: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    CHUNK_SIZE: 1024 * 1024 // 1MB分片大小
  }
}

// ================================
// HTTP状态码常量
// ================================
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
}

// ================================
// 业务状态码常量
// ================================
export const BUSINESS_CODE = {
  SUCCESS: 200,
  FAIL: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  SERVER_ERROR: 500,

  // 自定义业务状态码
  TOKEN_EXPIRED: 4001,
  TOKEN_INVALID: 4002,
  USER_NOT_FOUND: 4003,
  PASSWORD_ERROR: 4004,
  ACCOUNT_DISABLED: 4005,
  PERMISSION_DENIED: 4006,
  RESOURCE_NOT_FOUND: 4007,
  DUPLICATE_RESOURCE: 4008,
  QUOTA_EXCEEDED: 4009,
  RATE_LIMIT_EXCEEDED: 4010
}

// ================================
// API端点常量
// ================================
export const API_ENDPOINTS = {
  // 用户相关
  USER: {
    LOGIN: '/user/login',
    REGISTER: '/user/register',
    LOGOUT: '/user/logout',
    INFO: '/user/info',
    UPDATE_INFO: '/user/info',
    CHANGE_PASSWORD: '/user/password',
    RESET_PASSWORD: '/user/reset-password',
    VERIFY_EMAIL: '/user/verify-email'
  },

  // 公司信息
  COMPANY: {
    INFO: '/company/info',
    SERVICES: '/company/services',
    SERVICE_DETAIL: '/company/services/:id',
    HISTORY: '/company/history',
    TEAM: '/company/team'
  },

  // 新闻资讯
  NEWS: {
    LIST: '/news/list',
    DETAIL: '/news/:id',
    HOT: '/news/hot',
    CATEGORIES: '/news/categories',
    SEARCH: '/news/search'
  },

  // 招聘信息
  JOBS: {
    LIST: '/jobs/list',
    DETAIL: '/jobs/:id',
    APPLY: '/jobs/apply',
    CATEGORIES: '/jobs/categories',
    LOCATIONS: '/jobs/locations'
  },

  // 联系我们
  CONTACT: {
    SUBMIT: '/contact/submit',
    INFO: '/contact/info',
    FEEDBACK: '/contact/feedback'
  },

  // 软件产品
  SOFTWARE: {
    LIST: '/software/list',
    BIG_DATA: '/software/bigdata',
    SMART_TOWN: '/software/smarttown',
    SOLUTIONS: '/software/solutions'
  },

  // 文件上传
  UPLOAD: {
    FILE: '/upload/file',
    FILES: '/upload/files',
    DELETE: '/upload/file/:id',
    CHUNK: '/upload/chunk',
    MERGE: '/upload/merge'
  }
}

// ================================
// 错误消息常量
// ================================
export const ERROR_MESSAGES = {
  NETWORK_ERROR: '网络连接失败，请检查网络设置',
  TIMEOUT_ERROR: '请求超时，请稍后重试',
  SERVER_ERROR: '服务器内部错误，请稍后重试',
  UNAUTHORIZED: '登录已过期，请重新登录',
  FORBIDDEN: '没有权限访问该资源',
  NOT_FOUND: '请求的资源不存在',
  VALIDATION_ERROR: '数据验证失败',
  UPLOAD_ERROR: '文件上传失败',
  DOWNLOAD_ERROR: '文件下载失败',
  UNKNOWN_ERROR: '未知错误，请稍后重试'
}

// ================================
// 请求头常量
// ================================
export const REQUEST_HEADERS = {
  CONTENT_TYPE: {
    JSON: 'application/json',
    FORM: 'application/x-www-form-urlencoded',
    MULTIPART: 'multipart/form-data',
    TEXT: 'text/plain'
  },

  ACCEPT: {
    JSON: 'application/json',
    XML: 'application/xml',
    TEXT: 'text/plain',
    HTML: 'text/html'
  }
}

// ================================
// 缓存键常量
// ================================
export const CACHE_KEYS = {
  USER_INFO: 'user_info',
  COMPANY_INFO: 'company_info',
  NEWS_LIST: 'news_list',
  HOT_NEWS: 'hot_news',
  JOB_LIST: 'job_list',
  SOFTWARE_LIST: 'software_list',
  CONTACT_INFO: 'contact_info'
}

// ================================
// 存储键常量
// ================================
export const STORAGE_KEYS = {
  TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_INFO: 'user_info',
  LANGUAGE: 'language',
  THEME: 'theme',
  SETTINGS: 'user_settings'
}

// ================================
// 分页配置
// ================================
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_SIZE: 10,
  MAX_SIZE: 100,
  SIZE_OPTIONS: [10, 20, 50, 100]
}

// ================================
// 工具函数
// ================================

/**
 * 构建完整的API URL
 * @param {string} endpoint API端点
 * @param {object} params 路径参数
 * @returns {string} 完整的URL
 */
export const buildApiUrl = (endpoint, params = {}) => {
  let url = endpoint

  // 替换路径参数
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key])
  })

  return url
}

/**
 * 获取错误消息
 * @param {number} code 错误码
 * @param {string} defaultMessage 默认消息
 * @returns {string} 错误消息
 */
export const getErrorMessage = (code, defaultMessage = ERROR_MESSAGES.UNKNOWN_ERROR) => {
  const messageMap = {
    [HTTP_STATUS.UNAUTHORIZED]: ERROR_MESSAGES.UNAUTHORIZED,
    [HTTP_STATUS.FORBIDDEN]: ERROR_MESSAGES.FORBIDDEN,
    [HTTP_STATUS.NOT_FOUND]: ERROR_MESSAGES.NOT_FOUND,
    [HTTP_STATUS.INTERNAL_SERVER_ERROR]: ERROR_MESSAGES.SERVER_ERROR,
    [BUSINESS_CODE.TOKEN_EXPIRED]: ERROR_MESSAGES.UNAUTHORIZED,
    [BUSINESS_CODE.TOKEN_INVALID]: ERROR_MESSAGES.UNAUTHORIZED,
    [BUSINESS_CODE.PERMISSION_DENIED]: ERROR_MESSAGES.FORBIDDEN
  }

  return messageMap[code] || defaultMessage
}

/**
 * 检查文件类型是否允许
 * @param {string} fileType 文件MIME类型
 * @returns {boolean} 是否允许
 */
export const isFileTypeAllowed = (fileType) => {
  return REQUEST_CONFIG.UPLOAD.ALLOWED_TYPES.includes(fileType)
}

/**
 * 检查文件大小是否超限
 * @param {number} fileSize 文件大小（字节）
 * @returns {boolean} 是否超限
 */
export const isFileSizeExceeded = (fileSize) => {
  return fileSize > REQUEST_CONFIG.UPLOAD.MAX_FILE_SIZE
}

/**
 * 格式化文件大小
 * @param {number} bytes 字节数
 * @returns {string} 格式化后的大小
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 生成请求ID
 * @returns {string} 唯一请求ID
 */
export const generateRequestId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * 判断是否为开发环境
 * @returns {boolean}
 */
export const isDevelopment = () => {
  return ENV === 'development'
}

/**
 * 判断是否为生产环境
 * @returns {boolean}
 */
export const isProduction = () => {
  return ENV === 'production'
}

// 默认导出配置对象
export default {
  ENV,
  getBaseURL,
  REQUEST_CONFIG,
  HTTP_STATUS,
  BUSINESS_CODE,
  API_ENDPOINTS,
  ERROR_MESSAGES,
  REQUEST_HEADERS,
  CACHE_KEYS,
  STORAGE_KEYS,
  PAGINATION,
  buildApiUrl,
  getErrorMessage,
  isFileTypeAllowed,
  isFileSizeExceeded,
  formatFileSize,
  generateRequestId,
  isDevelopment,
  isProduction
}



// WEBPACK FOOTER //
// ./src/api/config.js